import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Check, LoaderCircle } from "lucide-react";
import { BONUSES, CONSENT_TEXT, STATES, JOURNEYS, captureAttribution } from "../content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const RegistrationForm = ({ idPrefix, showBonuses = false }) => {
  const navigate = useNavigate();
  const attribution = useMemo(() => captureAttribution(), []);
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", phone: "", state: "", journey: "" });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!consent) { setError("Please check the consent box to register."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/ghl-lead`, {
        ...form,
        journey: form.journey || null,
        consent,
        consentText: CONSENT_TEXT,
        consentTimestamp: new Date().toISOString(),
        ...attribution,
      });
      navigate("/thank-you");
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border border-white/10 bg-white/[0.03] backdrop-blur-xl p-6 md:p-8">
      {showBonuses && (
        <>
          <p className="font-mono-c text-[10px] tracking-[0.35em] text-[#5D8FC4] uppercase" data-testid={`${idPrefix}-dont-miss-label`}>Don't Miss Out</p>
          <h3 className="text-lg font-bold mt-3">When You Register, You'll Receive:</h3>
          <ul className="mt-4 space-y-2.5">
            {BONUSES.map((b, i) => (
              <li key={i} className="flex items-start gap-2.5 text-[13px] text-white/70">
                <Check className="w-4 h-4 text-[#5D8FC4] mt-0.5 shrink-0" strokeWidth={3} />
                {b}
              </li>
            ))}
          </ul>
          <p className="text-[22px] font-black text-[#5D8FC4] mt-4">FREE!</p>
          <div className="h-px bg-white/10 my-5" />
        </>
      )}
      <form onSubmit={submit} className="space-y-3" data-testid={`${idPrefix}-registration-form`}>
        <div className="grid grid-cols-2 gap-3">
          <input required data-testid={`${idPrefix}-first-name-input`} className="field-input" placeholder="First name" value={form.firstName} onChange={set("firstName")} />
          <input required data-testid={`${idPrefix}-last-name-input`} className="field-input" placeholder="Last name" value={form.lastName} onChange={set("lastName")} />
        </div>
        <input required type="email" data-testid={`${idPrefix}-email-input`} className="field-input" placeholder="Email address" value={form.email} onChange={set("email")} />
        <input required type="tel" data-testid={`${idPrefix}-phone-input`} className="field-input" placeholder="Mobile phone" value={form.phone} onChange={set("phone")} />
        <select required data-testid={`${idPrefix}-state-select`} className="field-input" value={form.state} onChange={set("state")}>
          <option value="" disabled>State *</option>
          {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid={`${idPrefix}-journey-select`} className="field-input" value={form.journey} onChange={set("journey")}>
          <option value="">Where are you in your homebuying journey? (optional)</option>
          {JOURNEYS.map((j) => <option key={j} value={j}>{j}</option>)}
        </select>
        <label className="flex items-start gap-2.5 cursor-pointer pt-1">
          <input
            type="checkbox"
            data-testid={`${idPrefix}-consent-checkbox`}
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="mt-0.5 accent-[#5D8FC4] w-4 h-4 shrink-0"
          />
          <span className="text-[10px] leading-relaxed text-white/50">
            {CONSENT_TEXT.replace(" View our Privacy Policy and Terms and Conditions.", "")} View our{" "}
            <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="underline text-white/70 hover:text-[#5D8FC4] transition-colors duration-200" data-testid={`${idPrefix}-consent-privacy-link`}>
              Privacy Policy
            </a>{" "}and{" "}
            <a href="/terms-conditions" target="_blank" rel="noopener noreferrer" className="underline text-white/70 hover:text-[#5D8FC4] transition-colors duration-200" data-testid={`${idPrefix}-consent-terms-link`}>
              Terms and Conditions
            </a>.
          </span>
        </label>
        {error && <p data-testid={`${idPrefix}-form-error`} className="text-red-400 text-xs">{error}</p>}
        <button type="submit" disabled={loading} data-testid={`${idPrefix}-submit-button`} className="btn-accent w-full flex items-center justify-center gap-2 text-sm disabled:opacity-60">
          {loading ? <LoaderCircle className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" strokeWidth={3} />}
          Reserve My Free Seat
        </button>
        <p className="font-mono-c text-[10px] text-white/25 text-center tracking-wide">
          Free educational event · No obligation · No hard credit pull required to attend
        </p>
      </form>
    </div>
  );
};
